/**
 * Admin · Bulk User Cleanup (destructive).
 *
 * Two-phase: Preview first (never touches the DB), then Execute (requires
 * exact confirm token). Cascades through 80+ user-scoped collections.
 */
import { useState } from "react";
import { AlertTriangle, Trash2, Loader2, ShieldAlert, CheckCircle2, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const CONFIRM_TOKEN = "NUKE-EVERYONE-EXCEPT-ME";

export default function AdminUserCleanup() {
  const { user } = useAuth();
  const [preserveInput, setPreserveInput] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState(null);
  const [confirmTyped, setConfirmTyped] = useState("");

  const preserveEmails = preserveInput
    .split(/[\s,\n]+/)
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);

  const runPreview = async () => {
    setLoading(true);
    setResult(null);
    setPreview(null);
    setConfirmTyped("");
    try {
      const { data } = await api.post("/admin/users/cleanup/preview", {
        preserve_emails: preserveEmails,
      });
      setPreview(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Preview failed");
    } finally {
      setLoading(false);
    }
  };

  const runExecute = async () => {
    if (confirmTyped !== CONFIRM_TOKEN) {
      toast.error(`Type exactly: ${CONFIRM_TOKEN}`);
      return;
    }
    if (!window.confirm(`This will permanently delete ${preview.would_delete_users} users and ${preview.cascade_total_docs.toLocaleString()} associated documents. There is NO undo. Continue?`)) {
      return;
    }
    setExecuting(true);
    try {
      const { data } = await api.post("/admin/users/cleanup/execute", {
        preserve_emails: preserveEmails,
        confirm: CONFIRM_TOKEN,
      });
      setResult(data);
      setPreview(null);
      toast.success(`Deleted ${data.users_deleted} users + ${data.cascade_deleted.toLocaleString()} associated records`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Execute failed");
    } finally {
      setExecuting(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="admin-user-cleanup">
      <div>
        <h1 className="text-2xl font-black flex items-center gap-2">
          <Trash2 size={24} className="text-red-600" /> Bulk User Cleanup
        </h1>
        <p className="text-sm text-[var(--ink-3)] mt-1">
          Wipe every user and their associated data — except the emails you list below. Your own account (<b>{user?.email}</b>) is always auto-preserved.
        </p>
      </div>

      {/* ── Danger banner */}
      <div className="rounded-xl border-2 border-red-300 bg-red-50 p-4 flex gap-3">
        <AlertTriangle className="text-red-600 shrink-0 mt-0.5" size={20} />
        <div className="text-sm text-red-900 leading-relaxed">
          <b>This is a destructive, non-reversible operation.</b> It deletes users AND cascades to all their downstream data: leads, deals, deal rooms, sent emails, subscriptions, buyer profiles, forum posts, etc. Always run <b>Preview</b> first. There is no undo.
        </div>
      </div>

      {/* ── Input */}
      <div className="card space-y-3" data-testid="cleanup-preserve-card">
        <div>
          <label className="label-eyebrow mb-2 block">Emails to preserve (one per line, or comma-separated)</label>
          <textarea
            value={preserveInput}
            onChange={(e) => setPreserveInput(e.target.value)}
            placeholder={"tracy.perrin1122@gmail.com\ncommunity@ispeedtoclose.com   (leave this to keep the system community poster)"}
            className="input min-h-[110px] font-mono text-sm"
            data-testid="cleanup-preserve-input"
          />
          <div className="text-xs text-[var(--ink-3)] mt-2">
            {preserveEmails.length === 0
              ? <>Only your own account (<b>{user?.email}</b>) will be preserved.</>
              : <>{preserveEmails.length} email{preserveEmails.length !== 1 && "s"} listed + your own account.</>}
          </div>
        </div>
        <button
          onClick={runPreview}
          disabled={loading}
          className="btn btn-primary inline-flex items-center gap-2"
          data-testid="cleanup-preview-btn"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <ShieldAlert size={14} />}
          {loading ? "Analyzing…" : "Preview what will be deleted"}
        </button>
      </div>

      {/* ── Preview result */}
      {preview && (
        <div className="card border-2 border-amber-300 bg-amber-50/50 space-y-4" data-testid="cleanup-preview-result">
          <div className="flex items-baseline justify-between gap-4">
            <div>
              <div className="label-eyebrow text-amber-700">Preview · nothing has been deleted yet</div>
              <h2 className="text-xl font-black mt-1">
                {preview.would_delete_users.toLocaleString()} users + {preview.cascade_total_docs.toLocaleString()} associated docs would be deleted
              </h2>
            </div>
            <div className="text-right">
              <div className="text-3xl font-black text-emerald-700">{preview.would_keep_users}</div>
              <div className="text-xs uppercase tracking-widest text-emerald-800">preserved</div>
            </div>
          </div>

          {/* Preserved list */}
          <div className="grid gap-1.5" data-testid="cleanup-preserved-list">
            {preview.preserved.map((p) => (
              <div key={p.id} className="flex items-center gap-2 text-sm bg-white border border-emerald-300 rounded-lg px-3 py-2">
                <CheckCircle2 size={14} className="text-emerald-600 shrink-0" />
                <span className="font-mono truncate">{p.email || "(no email)"}</span>
                {p.role === "admin" && <span className="text-[10px] bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded font-bold uppercase">Admin</span>}
                {p.role === "system" && <span className="text-[10px] bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded font-bold uppercase">System</span>}
              </div>
            ))}
          </div>

          {/* Warnings */}
          {(preview.warnings.admin_role_targets > 0 || preview.warnings.system_role_targets > 0 || preview.warnings.active_subscription_targets > 0) && (
            <div className="rounded-lg border border-red-300 bg-red-50 p-3 space-y-3" data-testid="cleanup-warnings">
              <div className="flex items-center gap-2 font-bold text-sm text-red-800">
                <AlertTriangle size={14} /> Warnings — verify before proceeding
              </div>
              {preview.warnings.admin_role_targets > 0 && (
                <div className="text-sm text-red-900 space-y-2">
                  <div>
                    <b>{preview.warnings.admin_role_targets}</b> user{preview.warnings.admin_role_targets !== 1 && "s"} with <b>admin</b> role will be deleted.
                    {preview.preserved.filter((p) => p.role === "admin").length === 0 && (
                      <div className="font-bold mt-1 text-red-950">
                        ⛔ Zero admins would remain — the platform will refuse this and return an error. Add at least one admin email to preserve above.
                      </div>
                    )}
                  </div>
                  {preview.admin_targets_detail?.length > 0 && (
                    <div className="bg-white border border-red-200 rounded-md p-2 space-y-1" data-testid="cleanup-admin-detail">
                      <div className="text-[10px] font-bold uppercase tracking-widest text-red-700 mb-1">
                        Admin accounts that would be deleted:
                      </div>
                      {preview.admin_targets_detail.map((u) => (
                        <div key={u.id} className="flex items-center justify-between gap-2 text-xs">
                          <span className="font-mono truncate">
                            {u.email || u.id}
                            {u.subscription_status && u.subscription_status !== "trialing" && (
                              <span className="ml-1.5 text-[9px] bg-emerald-100 text-emerald-800 px-1 py-0.5 rounded font-bold uppercase">
                                {u.subscription_status}
                              </span>
                            )}
                          </span>
                          <button
                            type="button"
                            onClick={() => setPreserveInput((v) => (v ? v + "\n" : "") + u.email)}
                            className="text-[10px] font-bold bg-emerald-600 hover:bg-emerald-700 text-white px-2 py-0.5 rounded shrink-0"
                            data-testid={`preserve-admin-${u.id}`}
                          >
                            + Preserve
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {preview.warnings.system_role_targets > 0 && (
                <div className="text-sm text-red-900 space-y-2">
                  <div>
                    <b>{preview.warnings.system_role_targets}</b> user{preview.warnings.system_role_targets !== 1 && "s"} with <b>system</b> role will be deleted (may affect seeded community content, forum posts, etc).
                  </div>
                  {preview.system_targets_detail?.length > 0 && (
                    <div className="bg-white border border-red-200 rounded-md p-2 space-y-1" data-testid="cleanup-system-detail">
                      <div className="text-[10px] font-bold uppercase tracking-widest text-red-700 mb-1">
                        System accounts that would be deleted:
                      </div>
                      {preview.system_targets_detail.map((u) => (
                        <div key={u.id} className="flex items-center justify-between gap-2 text-xs">
                          <span className="font-mono truncate">
                            {u.email || u.id}{u.name && ` · ${u.name}`}
                          </span>
                          <button
                            type="button"
                            onClick={() => setPreserveInput((v) => (v ? v + "\n" : "") + u.email)}
                            className="text-[10px] font-bold bg-emerald-600 hover:bg-emerald-700 text-white px-2 py-0.5 rounded shrink-0"
                            data-testid={`preserve-system-${u.id}`}
                          >
                            + Preserve
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {preview.warnings.active_subscription_targets > 0 && (
                <div className="text-sm text-red-900">
                  <b>{preview.warnings.active_subscription_targets}</b> user{preview.warnings.active_subscription_targets !== 1 && "s"} have <b>active/trialing paid Stripe subscriptions</b>. Deleting them will orphan their Stripe records.
                </div>
              )}
              {(preview.warnings.admin_role_targets > 0 || preview.warnings.system_role_targets > 0) && (
                <div className="text-xs text-red-800 border-t border-red-200 pt-2">
                  Click <b>+ Preserve</b> on any row above, then click <b>Preview what will be deleted</b> again to refresh the counts.
                </div>
              )}
            </div>
          )}

          {/* Cascade table */}
          <div>
            <div className="label-eyebrow mb-2">Cascade — top collections affected</div>
            <div className="grid gap-1 text-xs font-mono max-h-52 overflow-y-auto">
              {preview.cascade_estimate.map((c) => (
                <div key={`${c.collection}.${c.field}`} className="flex justify-between bg-white/60 border border-amber-200 rounded px-2.5 py-1">
                  <span>{c.collection}.{c.field}</span>
                  <span className="font-bold text-amber-900">{c.count.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Sample users */}
          <details className="text-sm">
            <summary className="cursor-pointer font-bold text-[var(--ink-2)]">Sample of {preview.sample_users_to_delete.length} users to delete →</summary>
            <div className="mt-2 grid gap-1 text-xs font-mono max-h-64 overflow-y-auto">
              {preview.sample_users_to_delete.map((u) => (
                <div key={u.id} className="flex items-center gap-2 bg-white/60 border border-amber-200 rounded px-2.5 py-1">
                  {u.role === "admin" && <span className="text-[9px] bg-red-100 text-red-800 px-1 py-0.5 rounded font-bold">ADMIN</span>}
                  {u.role === "system" && <span className="text-[9px] bg-blue-100 text-blue-800 px-1 py-0.5 rounded font-bold">SYS</span>}
                  <span className="truncate">{u.email || u.id}</span>
                </div>
              ))}
            </div>
          </details>

          {/* Confirm + execute */}
          <div className="border-t border-amber-300 pt-4 space-y-3">
            <label className="block text-sm font-bold" htmlFor="cleanup-confirm-input">
              To execute, type <code className="bg-red-100 text-red-800 px-1.5 py-0.5 rounded font-mono select-all">{CONFIRM_TOKEN}</code> below:
            </label>
            <input
              id="cleanup-confirm-input"
              type="text"
              value={confirmTyped}
              onChange={(e) => setConfirmTyped(e.target.value)}
              placeholder={`Type ${CONFIRM_TOKEN} here to unlock the delete button`}
              className={`input font-mono text-sm ${confirmTyped === CONFIRM_TOKEN ? "border-emerald-500 ring-1 ring-emerald-300" : ""}`}
              autoComplete="off"
              spellCheck="false"
              autoCapitalize="off"
              autoCorrect="off"
              data-testid="cleanup-confirm-input"
            />
            {confirmTyped && confirmTyped !== CONFIRM_TOKEN && (
              <div className="text-xs text-red-700 font-semibold">
                Not quite — token must be exactly <code className="font-mono">{CONFIRM_TOKEN}</code> (all caps, with dashes). {confirmTyped.length !== CONFIRM_TOKEN.length && <>You typed {confirmTyped.length} chars, expected {CONFIRM_TOKEN.length}.</>}
              </div>
            )}
            {confirmTyped === CONFIRM_TOKEN && (
              <div className="text-xs text-emerald-700 font-semibold flex items-center gap-1">
                <CheckCircle2 size={12} /> Confirm token matches — delete button unlocked.
              </div>
            )}
            <button
              onClick={runExecute}
              disabled={executing || confirmTyped !== CONFIRM_TOKEN}
              className="w-full inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 disabled:bg-red-300 disabled:cursor-not-allowed text-white font-black py-3 rounded-lg transition"
              data-testid="cleanup-execute-btn"
            >
              {executing ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
              {executing
                ? "Executing…"
                : confirmTyped !== CONFIRM_TOKEN
                ? `Type the confirm token above to unlock`
                : `Permanently delete ${preview.would_delete_users} users + ${preview.cascade_total_docs.toLocaleString()} docs`}
            </button>
          </div>
        </div>
      )}

      {/* ── Result */}
      {result && (
        <div className="card border-2 border-emerald-400 bg-emerald-50 space-y-3" data-testid="cleanup-result">
          <div className="flex items-center gap-2 text-emerald-800 font-black text-lg">
            <CheckCircle2 size={20} /> Cleanup complete
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Stat label="Users deleted" value={result.users_deleted.toLocaleString()} />
            <Stat label="Associated docs deleted" value={result.cascade_deleted.toLocaleString()} />
          </div>
          <details className="text-xs">
            <summary className="cursor-pointer font-bold text-emerald-800">Per-collection breakdown ({Object.keys(result.cascade_breakdown).length}) →</summary>
            <div className="mt-2 grid gap-1 font-mono max-h-72 overflow-y-auto">
              {Object.entries(result.cascade_breakdown).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                <div key={k} className="flex justify-between bg-white border border-emerald-200 rounded px-2.5 py-1">
                  <span>{k}</span>
                  <span className="font-bold">{v.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </details>
          <div className="text-xs text-emerald-800 flex items-center gap-1.5">
            <ExternalLink size={11} /> Audited to <code>admin_audit_log</code> as <code>users.bulk_cleanup</code>.
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-white border border-emerald-200 rounded-lg p-3">
      <div className="text-xs uppercase tracking-widest text-emerald-700 font-bold">{label}</div>
      <div className="text-2xl font-black text-emerald-900 mt-0.5">{value}</div>
    </div>
  );
}
